import type {
  DlqMessageSummary,
  WorkflowLifecycleEvent,
  WorkflowLogEntry,
  WorkflowRunStatus,
  WorkflowStepDetail,
  WorkflowStepStatus,
  WorkflowStepTimelineEntry,
  WorkflowTimelineEntry,
} from '@dev-console/types';
import type { ReactNode } from 'react';

import { StatusBadge as BaseStatusBadge } from '@/components/ui/status-badge';
import { LOG_LEVEL_COLORS, formatTimestamp } from '@/lib/utils';

export const statusStyles: Record<WorkflowRunStatus, string> = {
  failed: 'bg-red-100 text-red-800',
  halted: 'bg-amber-100 text-amber-800',
  retrying: 'bg-orange-100 text-orange-800',
  running: 'bg-blue-100 text-blue-800',
  succeeded: 'bg-emerald-100 text-emerald-800',
  unknown: 'bg-neutral-100 text-neutral-700',
};

const stepStatusStyles: Record<WorkflowStepStatus, string> = {
  failed: 'bg-red-100 text-red-800',
  halted: 'bg-amber-100 text-amber-800',
  running: 'bg-blue-100 text-blue-800',
  succeeded: 'bg-emerald-100 text-emerald-800',
  unknown: 'bg-neutral-100 text-neutral-700',
};

const runLifecycleStatusStyles: Record<string, string> = {
  created: 'bg-neutral-100 text-neutral-700',
  ...statusStyles,
};

const detailsBodyClass = 'border-t border-neutral-100 p-3';
const payloadClass = 'max-h-80 overflow-auto overscroll-auto rounded bg-neutral-50 p-3 text-xs text-neutral-700';
const errorStackClass =
  'mb-3 max-h-80 overflow-auto overscroll-auto rounded border border-red-100 bg-red-50 p-3 text-xs text-red-800';
const workflowLogMessagePreviewLength = 240;
const dlqMessagePreviewLength = 160;

export function formatDuration(durationMs: number | null): string {
  if (durationMs == null) return '-';
  if (durationMs < 1000) return `${durationMs}ms`;
  const seconds = Math.round(durationMs / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
}

function formatPreviewValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'object' && value != null) return JSON.stringify(value);
  return String(value);
}

export function getContextPreview(context: Record<string, unknown>, maxEntries?: number): string {
  const entries = Object.entries(context).filter(([, value]) => value != null);
  const visibleEntries = maxEntries == null ? entries : entries.slice(0, maxEntries);

  if (visibleEntries.length === 0) return '-';
  return visibleEntries.map(([key, value]) => `${key}: ${formatPreviewValue(value)}`).join(' · ');
}

export function WorkflowContextMetadata({ context }: { context: Record<string, unknown> }) {
  const entries = Object.entries(context).filter(([, value]) => value != null);

  if (entries.length === 0) {
    return <div className='text-sm text-neutral-500'>No metadata was found for this run.</div>;
  }

  return (
    <div className='flex flex-wrap gap-2'>
      {entries.map(([key, value]) => (
        <div
          key={key}
          className='min-w-0 max-w-full rounded border border-neutral-200 bg-white px-2.5 py-1.5 text-xs leading-5'
        >
          <span className='mr-1 text-neutral-500'>{key}</span>
          <span className='break-all font-medium text-neutral-900'>{formatPreviewValue(value)}</span>
        </div>
      ))}
    </div>
  );
}

export function StatusBadge({ status }: { status: WorkflowRunStatus }) {
  return <BaseStatusBadge status={status} colors={statusStyles} />;
}

function StepStatusBadge({ status }: { status: WorkflowStepStatus }) {
  return <BaseStatusBadge status={status} colors={stepStatusStyles} />;
}

function formatJson(value: unknown): string {
  if (value === undefined) return 'undefined';
  try {
    return JSON.stringify(value, null, 2) ?? String(value);
  } catch {
    return String(value);
  }
}

function getWorkflowLogMessagePreview(message: string): string {
  const normalizedMessage = message.replace(/\s+/g, ' ').trim();
  if (normalizedMessage.length <= workflowLogMessagePreviewLength) return normalizedMessage;
  return `${normalizedMessage.slice(0, workflowLogMessagePreviewLength)}...`;
}

export function JsonDetails({ defaultOpen, title, value }: { defaultOpen?: boolean; title: string; value: unknown }) {
  return (
    <details open={defaultOpen} className='min-w-0 rounded border border-neutral-200 bg-white'>
      <summary className='font-mediumbold cursor-pointer px-3 py-2 text-sm text-neutral-900'>{title}</summary>
      <div className='border-t border-neutral-100 p-3'>
        <pre className={payloadClass}>{formatJson(value)}</pre>
      </div>
    </details>
  );
}

function RawJsonDetails({ defaultOpen, title, value }: { defaultOpen?: boolean; title: string; value: unknown }) {
  return (
    <details open={defaultOpen} className='mt-2'>
      <summary className='cursor-pointer text-xs text-neutral-500'>{title}</summary>
      <pre className={`mt-2 ${payloadClass}`}>{formatJson(value)}</pre>
    </details>
  );
}

function StepLifecycleEvent({ event }: { event: WorkflowLifecycleEvent }) {
  return (
    <div className='border-t border-neutral-100 py-3 first:border-t-0 first:pt-0 last:pb-0'>
      <div className='flex flex-wrap items-center gap-2'>
        <span className='text-xs text-neutral-500'>{formatTimestamp(event.timestamp)}</span>
        <span className='rounded bg-neutral-100 px-2 py-0.5 text-xs text-neutral-800'>{event.phase}</span>
        {event.failureType && <span className='text-xs text-red-700'>{event.failureType}</span>}
      </div>
      <RawJsonDetails title='Raw event' value={event.payload} />
    </div>
  );
}

function WorkflowLogEntryRow({ log }: { log: WorkflowLogEntry }) {
  const messagePreview = getWorkflowLogMessagePreview(log.message || 'Workflow log');
  const level = log.level || 'log';
  const levelClass = LOG_LEVEL_COLORS[level]?.badge ?? 'bg-neutral-100 text-neutral-700';

  return (
    <div className='border-t border-neutral-100 py-3 first:border-t-0 first:pt-0 last:pb-0'>
      <div className='flex flex-wrap items-center gap-2'>
        <span className='text-xs text-neutral-500'>{formatTimestamp(log.timestamp)}</span>
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${levelClass}`}>{level}</span>
        <span className='min-w-0 break-words text-sm text-neutral-900'>{messagePreview}</span>
      </div>
      <RawJsonDetails title='Raw log' value={log.payload} />
    </div>
  );
}

function WorkflowStepTimelineRow({ entry }: { entry: WorkflowStepTimelineEntry }) {
  if (entry.type === 'lifecycle') {
    return <StepLifecycleEvent event={entry.event} />;
  }

  return <WorkflowLogEntryRow log={entry.log} />;
}

function getDlqMessagePreview(message: DlqMessageSummary): string {
  const normalizedBody = message.body.replace(/\s+/g, ' ').trim();
  if (normalizedBody.length <= dlqMessagePreviewLength) return normalizedBody;
  return `${normalizedBody.slice(0, dlqMessagePreviewLength)}...`;
}

function StepDlqMessages({
  messages,
  onRedrive,
}: {
  messages: DlqMessageSummary[];
  onRedrive: (messages: DlqMessageSummary[]) => void;
}) {
  if (messages.length === 0) return null;

  return (
    <div className='mb-4 border-b border-neutral-100 pb-4'>
      <div className='mb-2 flex flex-wrap items-center justify-between gap-2'>
        <div className='font-mediumbold text-xs uppercase text-red-700'>WorkflowEngine DLQ ({messages.length})</div>
        <button
          type='button'
          onClick={() => onRedrive(messages)}
          className='rounded-md border border-red-300 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100'
        >
          Redrive {messages.length}
        </button>
      </div>
      <div className='divide-y divide-neutral-100 rounded border border-neutral-200'>
        {messages.map((message) => (
          <div key={message.messageId} className='p-3'>
            <div className='flex flex-wrap items-center gap-2 text-xs'>
              <span className='font-mono text-neutral-700'>{message.jobName || 'unknown job'}</span>
              {message.traceId && <span className='font-mono text-neutral-500'>{message.traceId}</span>}
              <span className='text-neutral-500'>{formatTimestamp(message.sentTimestamp)}</span>
              <span className='ml-auto text-neutral-500'>receives {message.receiveCount}</span>
            </div>
            <div className='mt-2 break-words text-xs text-neutral-600'>{getDlqMessagePreview(message)}</div>
            <RawJsonDetails
              title='Raw DLQ message'
              value={{
                body: message.body,
                messageAttributes: message.messageAttributes,
                messageId: message.messageId,
                receiveCount: message.receiveCount,
                sentTimestamp: message.sentTimestamp,
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function TimelineField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className='text-[11px] uppercase tracking-wide text-neutral-400'>{label}</div>
      <div className='mt-1 min-h-[22px] text-sm text-neutral-700'>{children}</div>
    </div>
  );
}

function WorkflowStepRow({
  dlqMessages,
  onRedriveDlqMessages,
  step,
}: {
  dlqMessages: DlqMessageSummary[];
  onRedriveDlqMessages: (messages: DlqMessageSummary[]) => void;
  step: WorkflowStepDetail;
}) {
  const hasDlqMessages = dlqMessages.length > 0;

  return (
    <details className='group min-w-0 bg-white'>
      <summary className='grid cursor-pointer gap-3 p-3 hover:bg-neutral-50 md:grid-cols-[minmax(0,1fr)_140px_170px_110px]'>
        <div className='min-w-0'>
          <div className='flex flex-wrap items-center gap-2'>
            <span className='font-mediumbold break-words text-sm text-neutral-900'>{step.stepKey}</span>
            {hasDlqMessages && (
              <>
                <span className='rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700'>
                  {dlqMessages.length} DLQ
                </span>
                <button
                  type='button'
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onRedriveDlqMessages(dlqMessages);
                  }}
                  className='rounded-md border border-red-300 bg-white px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50'
                >
                  Redrive
                </button>
              </>
            )}
          </div>
          {step.failureMessage && <div className='mt-1 break-words text-sm text-red-700'>{step.failureMessage}</div>}
        </div>
        <TimelineField label='Status'>
          <StepStatusBadge status={step.status} />
        </TimelineField>
        <TimelineField label='Started'>{step.startedAt ? formatTimestamp(step.startedAt) : '-'}</TimelineField>
        <TimelineField label='Duration'>{formatDuration(step.durationMs)}</TimelineField>
      </summary>
      <div className={detailsBodyClass}>
        {step.errorStack && <pre className={errorStackClass}>{step.errorStack}</pre>}
        <StepDlqMessages messages={dlqMessages} onRedrive={onRedriveDlqMessages} />
        <div>
          <div className='font-mediumbold mb-2 text-xs uppercase text-neutral-400'>Step Activity</div>
          {step.timeline.length === 0 ? (
            <div className='text-sm text-neutral-500'>No activity was found for this step.</div>
          ) : (
            step.timeline.map((entry, index) => (
              <WorkflowStepTimelineRow key={`${entry.type}-${entry.timestamp}-${index}`} entry={entry} />
            ))
          )}
        </div>
      </div>
    </details>
  );
}

function getRunLifecycleStatus(event: WorkflowLifecycleEvent): string {
  if (event.phase === 'run.created') return 'created';
  if (event.completionType === 'success') return 'succeeded';
  if (event.completionType === 'failure') return 'failed';
  if (event.completionType === 'halt') return 'halted';
  return 'unknown';
}

function RunLifecycleRow({ entry }: { entry: Extract<WorkflowTimelineEntry, { type: 'run' }> }) {
  const { event } = entry;
  const status = getRunLifecycleStatus(event);

  return (
    <details className='group min-w-0 bg-white'>
      <summary className='grid cursor-pointer gap-3 p-3 hover:bg-neutral-50 md:grid-cols-[minmax(0,1fr)_140px_170px_110px]'>
        <div className='min-w-0'>
          <div className='font-mediumbold break-words text-sm text-neutral-900'>{event.phase}</div>
          <div className='mt-1 truncate text-sm text-neutral-500'>{event.workflowId}</div>
        </div>
        <TimelineField label='Status'>
          <BaseStatusBadge status={status} colors={runLifecycleStatusStyles} />
        </TimelineField>
        <TimelineField label='Started'>{formatTimestamp(event.timestamp)}</TimelineField>
        <TimelineField label='Duration'>-</TimelineField>
      </summary>
      <div className={detailsBodyClass}>
        {event.errorStack && <pre className={errorStackClass}>{event.errorStack}</pre>}
        <RawJsonDetails title='Raw event' value={event.payload} />
      </div>
    </details>
  );
}

export function WorkflowTimeline({
  dlqMessagesByStepKey = new Map(),
  isCheckingDlq,
  onRedriveDlqMessages,
  timeline,
}: {
  dlqMessagesByStepKey?: Map<string, DlqMessageSummary[]>;
  isCheckingDlq?: boolean;
  onRedriveDlqMessages: (messages: DlqMessageSummary[]) => void;
  timeline: WorkflowTimelineEntry[];
}) {
  return (
    <div className='min-w-0 overflow-hidden rounded border border-neutral-200 bg-white'>
      <div className='flex items-center justify-between gap-3 border-b border-neutral-100 bg-neutral-50 px-3 py-2'>
        <div className='font-mediumbold text-sm text-neutral-900'>Timeline</div>
        {isCheckingDlq && <div className='text-xs text-neutral-500'>Checking DLQ…</div>}
      </div>
      <div className='divide-y divide-neutral-100'>
        {timeline.length === 0 ? (
          <div className='p-3 text-sm text-neutral-500'>No timeline events were found for this run.</div>
        ) : (
          timeline.map((entry, index) =>
            entry.type === 'run' ? (
              <RunLifecycleRow key={`${entry.event.timestamp}-${entry.event.phase}-${index}`} entry={entry} />
            ) : (
              <WorkflowStepRow
                key={`${entry.step.stepKey}-${index}`}
                dlqMessages={dlqMessagesByStepKey.get(entry.step.stepKey) ?? []}
                onRedriveDlqMessages={onRedriveDlqMessages}
                step={entry.step}
              />
            )
          )
        )}
      </div>
    </div>
  );
}
